import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { CheckCircle, Award, Users, Globe, Factory, Target, Heart, TrendingUp, Download } from 'lucide-react';
import { certifications } from '../mockData';

export const About = () => {
  return (
    <div className="min-h-screen pt-32 pb-20">
      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-20">
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-red-600 text-white border-0">About Us</Badge>
          <h1 className="text-5xl font-bold text-gray-900 mb-6">Specialized in Excellence Since 1998</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Premier manufacturer & exporter of forceps, needle holders, and surgical scissors - serving healthcare professionals in 50+ countries worldwide
          </p>
          <Button 
            size="lg" 
            className="bg-red-600 hover:bg-red-700 text-white"
            onClick={() => {
              const link = document.createElement('a');
              link.href = '/catalogs/universa-surgical-brochure-2025.pdf';
              link.download = 'Universa-Surgical-Company-Brochure-2025.pdf';
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }}
          >
            <Download className="w-5 h-5 mr-2" />
            Download Company Brochure
          </Button>
        </div>

        {/* Company Image */}
        <div className="relative h-[400px] rounded-2xl overflow-hidden mb-12">
          <img
            src="https://images.unsplash.com/photo-1664902275922-9cd136203ba1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwyfHxtZWRpY2FsJTIwc3RlcmlsZXxlbnwwfHx8fDE3ODE1MTI3Mzd8MA&ixlib=rb-4.1.0&q=85"
            alt="Manufacturing facility"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 to-transparent" />
        </div>
      </div>

      {/* Our Story */}
      <section className="bg-gray-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Story</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Founded in 1998, Universa Surgical specializes in three core product categories: surgical forceps, 
                  needle holders, and surgical scissors. Our focused expertise allows us to deliver unmatched quality 
                  and precision in every instrument we manufacture.
                </p>
                <p>
                  Over the past 25 years, we have grown from a small specialized workshop to an internationally recognized 
                  manufacturer, exporting to over 50 countries across six continents. Our success is built on deep 
                  specialization, unwavering commitment to quality, and exceptional customer service.
                </p>
                <p>
                  Today, we offer over 200 specialized variations of forceps, needle holders, and scissors, each designed 
                  for specific surgical applications. Every instrument undergoes rigorous quality control and testing to 
                  ensure it meets international standards and exceeds surgeon expectations.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <Card className="p-6 border-0 bg-white">
                <Factory className="w-10 h-10 text-red-600 mb-4" />
                <div className="text-3xl font-bold text-gray-900 mb-2">25+</div>
                <div className="text-gray-600">Years in Business</div>
              </Card>
              <Card className="p-6 border-0 bg-white">
                <Globe className="w-10 h-10 text-red-600 mb-4" />
                <div className="text-3xl font-bold text-gray-900 mb-2">50+</div>
                <div className="text-gray-600">Countries Served</div>
              </Card>
              <Card className="p-6 border-0 bg-white">
                <Users className="w-10 h-10 text-red-600 mb-4" />
                <div className="text-3xl font-bold text-gray-900 mb-2">1000+</div>
                <div className="text-gray-600">Healthcare Partners</div>
              </Card>
              <Card className="p-6 border-0 bg-white">
                <Award className="w-10 h-10 text-red-600 mb-4" />
                <div className="text-3xl font-bold text-gray-900 mb-2">200+</div>
                <div className="text-gray-600">Specialized Products</div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Values</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="p-8 text-center border-0 hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Target className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="font-semibold text-xl text-gray-900 mb-3">Precision</h3>
              <p className="text-gray-600">
                Every instrument is crafted with meticulous attention to detail and accuracy
              </p>
            </Card>

            <Card className="p-8 text-center border-0 hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="font-semibold text-xl text-gray-900 mb-3">Quality</h3>
              <p className="text-gray-600">
                Uncompromising standards in materials, manufacturing, and testing processes
              </p>
            </Card>

            <Card className="p-8 text-center border-0 hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="font-semibold text-xl text-gray-900 mb-3">Innovation</h3>
              <p className="text-gray-600">
                Continuously improving and adopting new technologies and techniques
              </p>
            </Card>

            <Card className="p-8 text-center border-0 hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="font-semibold text-xl text-gray-900 mb-3">Care</h3>
              <p className="text-gray-600">
                Dedicated to supporting healthcare professionals in saving lives
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Manufacturing Process */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Manufacturing Process</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              From raw materials to finished products - every step ensures excellence
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              {
                step: '01',
                title: 'Material Selection',
                description: 'Premium medical-grade stainless steel and materials'
              },
              {
                step: '02',
                title: 'Precision Manufacturing',
                description: 'Advanced CNC machining and hand-finishing techniques'
              },
              {
                step: '03',
                title: 'Quality Testing',
                description: 'Rigorous inspection and performance verification'
              },
              {
                step: '04',
                title: 'Sterilization & Packaging',
                description: 'Medical-grade sterilization and secure packaging'
              }
            ].map((item) => (
              <Card key={item.step} className="p-6 border-0 relative overflow-hidden group hover:shadow-xl transition-shadow">
                <div className="text-6xl font-bold text-red-100 absolute -top-4 -right-4 group-hover:text-red-200 transition-colors">
                  {item.step}
                </div>
                <div className="relative z-10">
                  <h3 className="font-semibold text-lg text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-gray-600">{item.description}</p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Certifications & Compliance</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Meeting and exceeding international quality and safety standards
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {certifications.map((cert) => (
              <Card key={cert.name} className="p-8 text-center border-2 border-red-100 hover:border-red-600 hover:shadow-xl transition-all">
                <CheckCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
                <h4 className="font-bold text-lg text-gray-900 mb-2">{cert.name}</h4>
                <p className="text-sm text-gray-600">{cert.description}</p>
              </Card>
            ))}
          </div>

          <div className="mt-12 bg-red-50 rounded-xl p-8 text-center">
            <p className="text-gray-700 max-w-3xl mx-auto">
              All our surgical instruments are manufactured in compliance with ISO 13485:2016 standards, 
              FDA registered, and CE marked, ensuring the highest levels of quality, safety, and reliability 
              for medical professionals worldwide.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-red-600 to-red-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-4">Partner With Us</h2>
          <p className="text-xl mb-8 text-red-100">
            Join thousands of healthcare professionals who trust our instruments for their surgical needs
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-red-600 hover:bg-gray-100 w-full sm:w-auto">
                Contact Us
              </Button>
            </Link>
            <Link to="/products">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 w-full sm:w-auto">
                View Products
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
